import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { useAppSelector } from "../app/hooks";
import { toast } from "sonner";
import {
  downloadJobFile,
  fetchJobDetail,
  type JobListItem,
  updateJobStatus,
} from "../features/jobs/jobAPI";

interface JobStatus {
  inMatch: number;
  incomingRequests: number;
  inReview: number;
  shortlisted: number;
}

interface JobDetailsInfo {
  departmentName: string;
  industry: string;
  jobLevel: string;
  jobCoverage: string;
  teamManagement: string;
  nationality: string;
  validVisa: string;
  gender: string;
  drivingLicense: string;
  travelRequirement: string;
}

interface JobSection {
  title: string;
  experience: string;
  mustHaveSkills: string[];
  niceToHaveSkills: string[];
}

interface LanguageRequirement {
  language: string;
  speaking_level: string;
  reading_writing_level: string;
  level?: string;
}

interface EducationalRequirement {
  qualification: string;
  fieldOfStudy: string;
}

interface JobDetailView {
  id: number;
  title: string;
  location: string;
  jobType: string;
  workType: string;
  hiringManager: string;
  jobStatus: "Open" | "Close" | "On-hold";
  status: JobStatus;
  openTill: string;
  openedAt: string;
  datePosted: string;
  dateCreated: string;
  details: JobDetailsInfo;
  tasksDescription: string;
  sections: JobSection[];
  languageRequirements: LanguageRequirement[];
  educationalRequirements: EducationalRequirement[];
  certificationRequirements: string[];
}

const formatDisplayDate = (value?: string, pattern = "dd MMM yyyy") => {
  if (!value) return "N/A";
  try {
    return format(parseISO(value), pattern);
  } catch {
    return "N/A";
  }
};

const formatNationality = (
  nationality?: JobListItem["nationality"],
): string => {
  if (!nationality) return "N/A";
  if (Array.isArray(nationality)) {
    const names = nationality
      .map((item) => item?.name)
      .filter((name): name is string => Boolean(name));
    return names.length > 0 ? names.join(", ") : "N/A";
  }
  return nationality.name || "N/A";
};

const formatIndustry = (industry?: JobListItem["industry"]): string => {
  if (!Array.isArray(industry) || industry.length === 0) return "N/A";
  const names = industry
    .map((item) => item?.name)
    .filter((name): name is string => Boolean(name));
  return names.length > 0 ? names.join(", ") : "N/A";
};

const formatGender = (gender?: string): string => {
  if (!gender) return "No preference";
  const genderLabels: Record<string, string> = {
    M: "Male",
    F: "Female",
    NP: "No preference",
  };
  return genderLabels[gender] || gender;
};

const formatTeamManagement = (data: JobListItem): string => {
  if (Array.isArray(data.management_skills) && data.management_skills.length > 0) {
    return data.management_skills
      .map((skill) => {
        const name = skill.management_skill?.name || "N/A";
        const years = skill.years_of_experience;
        return years ? `${name} (${years} years)` : name;
      })
      .join(", ");
  }

  const peopleManagedRange = data.people_managed_range;
  if (
    peopleManagedRange &&
    typeof peopleManagedRange === "object" &&
    "name" in peopleManagedRange
  ) {
    return peopleManagedRange.name;
  }
  if (peopleManagedRange !== undefined && peopleManagedRange !== null && peopleManagedRange !== "") {
    return String(peopleManagedRange);
  }

  if (typeof data.manages_people === "boolean") {
    return data.manages_people ? "Yes" : "No";
  }

  return "N/A";
};

const hasMeaningfulValue = (value?: string | null): boolean => {
  const trimmed = (value ?? "").trim();
  return trimmed.length > 0 && trimmed !== "N/A";
};

const JobDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const accessToken = useAppSelector((state) => state.auth.accessToken);

  const [job, setJob] = useState<JobDetailView | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const getJobStatusSuccessMessage = (status: "Open" | "Close" | "On-hold") => {
    if (status === "Close") return "Hiring marked as complete.";
    if (status === "On-hold") return "Hiring put on hold.";
    return "Job reopened for hiring.";
  };

  const handleStatusUpdate = async (newStatus: "Open" | "Close" | "On-hold") => {
    if (!accessToken || !job) return;

    setIsUpdatingStatus(true);
    try {
      const result = await updateJobStatus(job.id, newStatus, accessToken);
      if (result.error) {
        toast.error(result.error);
        return;
      }

      setJob((prev) => (prev ? { ...prev, jobStatus: newStatus } : prev));
      toast.success(getJobStatusSuccessMessage(newStatus));
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to update job status.";
      toast.error(message);
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleNavigateToMatches = (
    tab: "In matches" | "Incoming request" | "In review" | "Shortlisted",
  ) => {
    if (!job) return;
    navigate("/job-matches", { state: { jobId: job.id, activeTab: tab } });
  };

  const handleDownload = async () => {
    if (!accessToken || !job) return;

    setIsDownloading(true);
    try {
      const result = await downloadJobFile(job.id, accessToken);
      if (result.error || !result.data) {
        toast.error(result.error || "Failed to download job file.");
        return;
      }

      const url = window.URL.createObjectURL(result.data.blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = result.data.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to download job file.";
      toast.error(message);
    } finally {
      setIsDownloading(false);
    }
  };

  useEffect(() => {
    const jobId = id ? Number(id) : NaN;
    if (!accessToken || !jobId || Number.isNaN(jobId)) return;

    const loadJob = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const response = await fetchJobDetail(jobId, accessToken);
        const data = response.data as JobListItem | undefined;
        if (!data) {
          setError("Job not found.");
          return;
        }

        const openTill = formatDisplayDate(data.closing_date, "dd MMM yyyy");
        const openedAt = formatDisplayDate(data.created_at, "dd MMM yyyy");
        const datePosted = formatDisplayDate(data.opening_date, "dd MMM yyyy");
        const dateCreated = formatDisplayDate(data.created_at, "dd MMM yyyy");

        const details: JobDetailsInfo = {
          departmentName: data.department?.name || "N/A",
          industry: formatIndustry(data.industry),
          jobLevel: data.job_level?.name || "N/A",
          jobCoverage: data.region?.name || "N/A",
          teamManagement: formatTeamManagement(data),
          nationality: formatNationality(data.nationality),
          validVisa: data.visa_status || "N/A",
          gender: formatGender(data.gender),
          drivingLicense: data.driving_license || "N/A",
          travelRequirement: data.travel_requirement || "N/A",
        };

        const sections: JobSection[] = Array.isArray(data.areas_of_expertise)
          ? data.areas_of_expertise.map((area: any) => ({
            title: area.field_of_expertise?.name || "N/A",
            experience: area.years_in_this_area_of_expertise
              ? `${area.years_in_this_area_of_expertise} years of experience`
              : "N/A",
            mustHaveSkills: Array.isArray(area.skills_must_have)
              ? area.skills_must_have.map((s: any) => s.name || "N/A")
              : [],
            niceToHaveSkills: Array.isArray(area.skills_nice_to_have)
              ? area.skills_nice_to_have.map((s: any) => s.name || "N/A")
              : [],
          }))
          : [];

        const languageRequirements: LanguageRequirement[] = Array.isArray(
          data.language_skills,
        )
          ? data.language_skills.map((ls: any) => ({
            language: ls.language?.name || "N/A",
            speaking_level: ls.speaking_level || "N/A",
            reading_writing_level: ls.reading_writing_level || "N/A",
            level:
              `${ls.speaking_level || ""}${ls.reading_writing_level
                ? ` - ${ls.reading_writing_level.toLowerCase()} reading / writing`
                : ""
                }`.trim() || "N/A",
          }))
          : [];

        const educationalRequirements: EducationalRequirement[] = Array.isArray(
          data.education,
        )
          ? data.education.map((edu: any) => ({
            qualification: edu.qualification?.name || "N/A",
            fieldOfStudy: edu.field_of_study?.name || "N/A",
          }))
          : [];

        const certificationRequirements: string[] = [
          ...(Array.isArray(data.certificate)
            ? data.certificate.map((cert: any) => cert.name || "N/A")
            : []),
          ...(Array.isArray((data as JobListItem & { other_certificates?: string[] }).other_certificates)
            ? (data as JobListItem & { other_certificates?: string[] }).other_certificates!
            : []),
        ].filter(Boolean);

        const hiringManagerName = data.hiring_manager
          ? `${data.hiring_manager.first_name} ${data.hiring_manager.middle_name || ""
            } ${data.hiring_manager.last_name}`.trim()
          : "N/A";

        const location = data.city?.name || "N/A";

        let jobStatus: "Open" | "Close" | "On-hold" = "Open";
        if (data.status === "Close" || data.status === "Closed") {
          jobStatus = "Close";
        } else if (data.status === "On-hold") {
          jobStatus = "On-hold";
        } else if (data.status === "Open") {
          jobStatus = "Open";
        }

        const viewJob: JobDetailView = {
          id: data.id,
          title: data.job_title || "N/A",
          location,
          jobType: Array.isArray(data.employment_type)
            ? data.employment_type.map((e: any) => e.name).join(", ")
            : "N/A",
          workType: Array.isArray(data.workplace_type)
            ? data.workplace_type.map((w: any) => w.name).join(", ")
            : "N/A",
          hiringManager: hiringManagerName,
          jobStatus,
          status: {
            inMatch: data.in_match ?? 0,
            incomingRequests: data.incoming_requests ?? 0,
            inReview: data.in_review ?? 0,
            shortlisted: data.shortlisted ?? 0,
          },
          openTill,
          openedAt,
          datePosted,
          dateCreated,
          details,
          tasksDescription: data.description || "",
          sections,
          languageRequirements,
          educationalRequirements,
          certificationRequirements,
        };

        setJob(viewJob);
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Failed to load job details.";
        setError(message);
      } finally {
        setIsLoading(false);
      }
    };

    loadJob();
  }, [id, accessToken]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white p-8 flex items-center justify-center">
        <p className="text-sm text-gray-500">Loading job details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white p-8 flex items-center justify-center">
        <p className="text-sm text-red-500">{error}</p>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="min-h-screen bg-white p-8 flex items-center justify-center">
        <p className="text-sm text-gray-500">Job not found.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white p-8">
      <div className="mx-auto max-w-7xl">
        {/* Header */}
        <div className="mb-6 flex items-start justify-between">
          <div className="flex flex-col">
            <h1 className="text-heading font-bold text-gray-900">{job.title}</h1>
            <p className="text-paragraph -mt-2 text-black">
              {job.location} - {job.jobType.toLowerCase()} - {job.workType.toLowerCase()}
            </p>
            <p className="text-paragraph">
              <span className="font-medium text-gray-400">Hiring manager - </span>
              <span className="font-bold text-black">{job.hiringManager}</span>
            </p>
          </div>

          {job.jobStatus !== "Close" && (
            <div className="flex gap-4 justify-evenly">
              <button
                type="button"
                className="flex items-center gap-2 rounded-md border border-gray-300 bg-white px-6 py-2.5 text-tiny font-normal text-black transition-colors hover:bg-black hover:text-white disabled:opacity-70"
                disabled={isUpdatingStatus}
                onClick={() => {
                  const nextStatus =
                    job.jobStatus === "On-hold" ? "Open" : "On-hold";
                  handleStatusUpdate(nextStatus);
                }}
              >
                {job.jobStatus === "On-hold" ? "Open" : "Hiring on hold"}
              </button>
              <button
                type="button"
                className="btn-cta flex items-center gap-2 rounded-md px-6 py-2.5 text-tiny font-normal disabled:opacity-70"
                disabled={isUpdatingStatus}
                onClick={() => {
                  handleStatusUpdate("Close");
                }}
              >
                Hiring complete
              </button>
            </div>
          )}
        </div>

        {/* Status Cards */}
        <div className="mb-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4 lg:gap-12">
          <button
            type="button"
            onClick={() => handleNavigateToMatches("In matches")}
            className="flex flex-col items-center rounded-md bg-[#E6EDDD] p-3 transition-opacity hover:opacity-90 cursor-pointer"
          >
            <p className="text-paragraph font-medium text-[#5D6675]">In matches</p>
            <p className="text-heading font-semibold text-[#192A47]">{job.status.inMatch}</p>
          </button>
          <button
            type="button"
            onClick={() => handleNavigateToMatches("Incoming request")}
            className="flex flex-col items-center rounded-md bg-[#CCD4DE] p-3 transition-opacity hover:opacity-90 cursor-pointer"
          >
            <p className="text-paragraph font-medium text-[#5D6675]">In coming requests</p>
            <p className="text-heading font-semibold text-[#192A47]">{job.status.incomingRequests}</p>
          </button>
          <button
            type="button"
            onClick={() => handleNavigateToMatches("In review")}
            className="flex flex-col items-center rounded-md bg-[#E2DDD4] p-3 transition-opacity hover:opacity-90 cursor-pointer"
          >
            <p className="text-paragraph font-medium text-[#5D6675]">In review</p>
            <p className="text-heading font-semibold text-[#192A47]">{job.status.inReview}</p>
          </button>
          <button
            type="button"
            onClick={() => handleNavigateToMatches("Shortlisted")}
            className="flex flex-col items-center rounded-md bg-[#C1C2C2] p-3 transition-opacity hover:opacity-90 cursor-pointer"
          >
            <p className="text-paragraph font-medium text-[#5D6675]">Shortlisted</p>
            <p className="text-heading font-semibold text-[#192A47]">{job.status.shortlisted}</p>
          </button>
        </div>

        <div className="mb-6 flex items-center justify-between border-b-2 pb-4">
          <div className="flex flex-col">
            <h2 className="text-subheading font-semibold">Job description</h2>
            <p className="text-tiny font-medium text-[#5D6675]">
            Opened at {job.openedAt} - Open until {job.openTill}  
            </p>
          </div>
          {job.jobStatus !== "Close" && (
            <button
              className="text-[#000000] hover:text.black"
              onClick={() => {
                navigate(`/edit-job/${job.id}`);
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="26"
                height="26"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 20h9" />
                <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z" />
              </svg>
            </button>
          )}
        </div>

        {/* Job Details Grid */}
        <div className="mb-8 grid grid-cols-5 gap-x-6 gap-y-6">
          <div>
            <p className="text-paragraph font-normal text-gray-400">Department name</p>
            <p className="text-paragraph font-bold">{job.details.departmentName}</p>
          </div>
          <div>
            <p className="text-paragraph font-normal text-gray-400">Industry</p>
            <p className="text-paragraph font-bold">{job.details.industry}</p>
          </div>
          <div>
            <p className="text-paragraph font-normal text-gray-400">Job level</p>
            <p className="text-paragraph font-bold">{job.details.jobLevel}</p>
          </div>
          <div>
            <p className="text-paragraph font-normal text-gray-400">Job coverage</p>
            <p className="text-paragraph font-bold">{job.details.jobCoverage}</p>
          </div>
          <div>
            <p className="text-paragraph font-normal text-gray-400">Team management experience</p>
            <p className="text-paragraph font-bold">{job.details.teamManagement}</p>
          </div>
          <div>
            <p className="text-paragraph font-normal text-gray-400">Nationality</p>
            <p className="text-paragraph font-bold">{job.details.nationality}</p>
          </div>
          <div>
            <p className="text-paragraph font-normal text-gray-400">Valid visa</p>
            <p className="text-paragraph font-bold">{job.details.validVisa}</p>
          </div>
          <div>
            <p className="text-paragraph font-normal text-gray-400">Gender</p>
            <p className="text-paragraph font-bold">{job.details.gender}</p>
          </div>
          <div>
            <p className="text-paragraph font-normal text-gray-400">Driving license</p>
            <p className="text-paragraph font-bold">{job.details.drivingLicense}</p>
          </div>
          <div>
            <p className="text-paragraph font-normal text-gray-400">Travel requirement</p>
            <p className="text-paragraph font-bold">{job.details.travelRequirement}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          {/* Left Column - Job Description */}
          <div className="lg:col-span-2">
            {job.tasksDescription.trim() && (
              <div className="mb-8">
                <h3 className="text-paragraph font-normal text-gray-400">
                  Tasks description
                </h3>
                <div className="space-y-3 text-paragraph font-medium leading-relaxed text-black">
                  {job.tasksDescription.split("\n").map((paragraph, index) =>
                    paragraph.trim() ? (
                      <p key={index}>{paragraph}</p>
                    ) : (
                      <br key={index} />
                    ),
                  )}
                </div>
              </div>
            )}

            {/* Skills Sections */}
            {job.sections.map((section, index) => {
              const mustHaveSkills = section.mustHaveSkills.filter(hasMeaningfulValue);
              const niceToHaveSkills = section.niceToHaveSkills.filter(hasMeaningfulValue);
              const hasMustHaveSkills = mustHaveSkills.length > 0;
              const hasNiceToHaveSkills = niceToHaveSkills.length > 0;
              const hasSectionContent =
                hasMeaningfulValue(section.title) ||
                hasMeaningfulValue(section.experience) ||
                hasMustHaveSkills ||
                hasNiceToHaveSkills;

              if (!hasSectionContent) return null;

              return (
              <div key={index} className="mb-8">
                {(hasMeaningfulValue(section.title) || hasMeaningfulValue(section.experience)) && (
                  <div className="mb-4 flex items-center justify-between">
                    {hasMeaningfulValue(section.title) && (
                      <h3 className="text-paragraph font-bold text-gray-900">{section.title}</h3>
                    )}
                    {hasMeaningfulValue(section.experience) && (
                      <span className="text-paragraph font-bold text-gray-900">{section.experience}</span>
                    )}
                  </div>
                )}

                {hasMustHaveSkills && (
                  <>
                    <h4 className="text-small mb-2 font-bold text-gray-900">Must have skills</h4>
                    <div className="mb-6 flex flex-wrap gap-2">
                      {mustHaveSkills.map((skill, idx) => (
                        <span
                          key={idx}
                          className="rounded-md border-2 border-black bg-white px-3 py-1.5 text-sm font-medium text-black"
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                  </>
                )}

                {hasNiceToHaveSkills && (
                  <>
                    <h4 className="text-small mb-2 font-bold text-gray-900">Nice to have skills</h4>
                    <div className="mb-6 flex flex-wrap gap-2">
                      {niceToHaveSkills.map((skill, idx) => (
                        <span
                          key={idx}
                          className="rounded-md border-2 border-black bg-white px-3 py-1.5 text-sm font-medium text-black"
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                  </>
                )}
              </div>
              );
            })}
          </div>

          {/* Right Column - Sidebar */}
          <div className="border-l-2 pl-4">
            {job.languageRequirements.length > 0 && (
              <div className="mb-8">
                <h3 className="text-subheading mb-4 pb-1 font-semibold border-b-2">
                  Language requirements
                </h3>
                {job.languageRequirements.map((lang, i) => (
                  <div key={i} className="mb-4">
                    <p className="text-paragraph font-normal">Language {i + 1}</p>
                    <p className="text-paragraph font-medium text-[#23272C]">
                      {lang.language} - Speaking: {lang.speaking_level} - Reading / Writing:{" "}
                      {lang.reading_writing_level}
                    </p>
                  </div>
                ))}
              </div>
            )}

            {job.educationalRequirements.length > 0 && (
              <div className="mb-8">
                <h3 className="text-subheading mb-4 pb-1 font-semibold border-b-2">
                  Educational requirements
                </h3>
                {job.educationalRequirements.map((edu, i) => (
                  <div key={i} className="mb-4">
                    <div className="flex justify-between">
                      <p className="text-paragraph font-normal">Qualification</p>
                      <p className="text-paragraph font-normal text-[#23272C]">Field of study</p>
                    </div>
                    <div className="flex justify-between">
                      <p className="text-paragraph font-bold text-[#23272C]">
                        {edu.qualification}
                      </p>
                      <p className="text-paragraph font-bold text-[#23272C]">
                        {edu.fieldOfStudy}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {job.certificationRequirements.length > 0 && (
              <div>
                <h3 className="text-subheading mb-4 pb-1 font-semibold border-b-2">
                  Certification requirements
                </h3>
                {job.certificationRequirements.map((cert, i) => (
                  <div key={i} className="mb-3">
                    <p className="text-paragraph font-bold text-[#23272C]">{cert}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="mt-8 flex justify-end gap-3">
          <button
            type="button"
            onClick={() => {
              navigate("/create-job", { state: { cloneJobId: job.id } });
            }}
            className="flex items-center gap-2 rounded-md border border-gray-300 bg-white px-6 py-2.5 text-tiny font-normal text-black transition-colors hover:bg-black hover:text-white disabled:opacity-70"
          >
            Clone Job
          </button>
          <button
            type="button"
            onClick={handleDownload}
            disabled={isDownloading}
            className="btn-cta flex items-center gap-2 rounded-md px-6 py-2.5 text-tiny font-normal disabled:opacity-70"
          >
            {isDownloading ? "Downloading..." : "Download"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default JobDetail;

